import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui';
import { cn } from '../../lib/utils';

export type SkipReasonValue = 'already_done' | 'not_applicable' | 'other_method';

interface Props {
  taskTitle: string;
  onConfirm: (reason: SkipReasonValue) => void;
  onClose: () => void;
}

const reasons: Array<{ value: SkipReasonValue; label: string; description: string }> = [
  {
    value: 'already_done',
    label: 'I already did this',
    description: 'Task was completed outside 301io or before the due date.',
  },
  {
    value: 'not_applicable',
    label: "This doesn't apply to my asset",
    description: 'This task isn\'t relevant for my specific appliance model or setup.',
  },
  {
    value: 'other_method',
    label: "I'll handle it another way",
    description: 'Hiring a technician or using a different process.',
  },
];

export function SkipReasonModal({ taskTitle, onConfirm, onClose }: Readonly<Props>) {
  const [selected, setSelected] = React.useState<SkipReasonValue | null>(null);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      {/* Backdrop — aria-hidden; keyboard users close via Escape (document listener) or X button */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl animate-in slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">
              Skip this task?
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 line-clamp-1">
              {taskTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="h-4 w-4 text-zinc-500" />
          </button>
        </div>

        {/* Reason list */}
        <div className="p-5 space-y-2">
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
            Why are you skipping?
          </p>
          {reasons.map((r) => (
            <label
              key={r.value}
              aria-label={r.label}
              className={cn(
                'flex items-start gap-3 cursor-pointer rounded-xl border p-3.5 transition-all duration-150',
                selected === r.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-border hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
              )}
            >
              <input
                type="radio"
                name="skip-reason"
                value={r.value}
                checked={selected === r.value}
                onChange={() => setSelected(r.value)}
                className="mt-0.5 accent-blue-600"
              />
              <div>
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                  {r.label}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {r.description}
                </p>
              </div>
            </label>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected}
            className="flex-1 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-200"
          >
            Skip Task
          </Button>
        </div>
      </div>
    </div>
  );
}
